import React from 'react';
import { Box, Button, SearchField } from '@drone-js/core';
import { connect } from 'react-redux';
import { OverlayType } from 'store/slice/overlay';
import { measureFinishRender } from 'core/Performance/PerformanceService';
import { NoOp } from 'component/common/ActivitiesConstants';
import { killEvent, notEnterOrSpaceKeydown } from 'core/utils/domUtils'; // Remove notEnterOrSpaceKeydown once integrate SearchBoxPopoverFeature
import { TextSearchType } from 'core/type/TextSearch.types';
import { pushHashToHistory } from 'core-framework/window/history';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesLandingSearchButtonClickEventActivitiesFieldsV4,
    ActivitiesLandingTextInputClickEventFieldsV2,
    ActivitiesOverlayTextSearchInputClearIconClickEventActivitiesFieldsV2,
    ActivitiesGlobalKeywordSearchClearClickEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesLandingSearchButtonClickEventActivitiesManifestV4,
    ActivitiesLandingTextInputClickEventManifestV2,
    ActivitiesOverlayTextSearchInputClearIconClickEventActivitiesManifestV2,
    ActivitiesGlobalKeywordSearchClearClickEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { mapDispatchToProps, mapStateToProps, SearchBoxPickerProps } from 'component/common/SearchBox/SearchBox.selectors';
import SearchBoxPopover from 'component/common/SearchBox/SearchBoxPopover';
import { FeatureName } from 'core/constant/FeatureName';
import Feature from 'component/common/Experiment/Feature';
import { ExperimentId } from 'core/type/ExperimentId';
import { useExperimentIsB } from 'component/common/Experiment/Experiment';

const SearchBox: React.FC<SearchBoxPickerProps> = (props) => {
    const { cms, query, inputRef, selected, pageTypeId, isSearchBoxDropdown, setSearchText, onSearchResultSelect, onGlobalKeywordResultSelect } = props;
    const shouldHideSearchBoxExperiment = useExperimentIsB(ExperimentId.ACT_4519);

    React.useEffect(() => {
        measureFinishRender({ component: 'SearchBox' }, pageTypeId);
    }, [pageTypeId]);

    // Remove once integrate SearchBoxPopoverFeature
    const onInputClickHandler = (e: React.MouseEvent | React.KeyboardEvent) => {
        if (notEnterOrSpaceKeydown(e)) return;
        trackElementEvent<ActivitiesLandingTextInputClickEventFieldsV2>(ActivitiesLandingTextInputClickEventManifestV2.ids, {
            action_element_value: query,
        });
        props.showOverlay(OverlayType.TextSearch);
        pushHashToHistory(OverlayType.TextSearch);
        killEvent(e);
    };

    const onPopoverInputClickHandler = () => {
        trackElementEvent<ActivitiesLandingTextInputClickEventFieldsV2>(ActivitiesLandingTextInputClickEventManifestV2.ids, {
            action_element_value: query,
        });
        props.showOverlay(OverlayType.TextSearch);
        pushHashToHistory(OverlayType.TextSearch);
    };

    const onSearchButtonClick = () => {
        const eventFieldProps = selected
            ? {
                  city_id: selected.cityId,
                  country_code_iso2: selected.countryISO2 && selected.countryISO2.toUpperCase(),
                  activity_id: selected.activityId || undefined,
                  supplier_id: selected.supplierId || undefined,
                  action_element_value: TextSearchType[selected.type],
              }
            : undefined;
        trackElementEvent<ActivitiesLandingSearchButtonClickEventActivitiesFieldsV4>(ActivitiesLandingSearchButtonClickEventActivitiesManifestV4.ids, {}, eventFieldProps);
        props.isGlobalKeywordSearch && query.length ? onGlobalKeywordResultSelect(query) : props.onSearchButtonClick();
    };

    const onClearTextInputClick = () => {
        if (props.isGlobalKeywordSearch) {
            trackElementEvent<ActivitiesGlobalKeywordSearchClearClickEventActivitiesFieldsV1>(ActivitiesGlobalKeywordSearchClearClickEventActivitiesManifestV1.ids, {
                search_phrase: query,
            });
        } else {
            trackElementEvent<ActivitiesOverlayTextSearchInputClearIconClickEventActivitiesFieldsV2>(ActivitiesOverlayTextSearchInputClearIconClickEventActivitiesManifestV2.ids, {
                search_phrase: query,
            });
        }
        props.setSearchText('');
        props.setSelectedResult(undefined);
    };

    const searchButtonVariant = props.screenSize.desktop ? 'label' : 'icon';
    const searchButtonLeadingIcon = props.screenSize.desktop ? undefined : 'fill.symbol.search';

    return (
        <>
            <Feature.Off name={FeatureName.HomePageV2Layout}>
                <Box
                    data-testid="search-box-wrapper"
                    backgroundColor="base.surface"
                    display="flex"
                    flexDirection="column"
                    borderRadius="8"
                    boxShadow="center-s"
                    maxWidth={isSearchBoxDropdown ? '744px' : '456px'}
                    width="100%"
                    p="12"
                >
                    {!props.isSearchBoxDropdown ? (
                        <SearchField
                            data-testid="activities-landing-text-input"
                            data-element-name="activities-landing-text-input"
                            placeholderText={props.placeholderText}
                            trailingAsset={{ id: 'line.symbol.search' }}
                            label={props.placeholderText}
                            clearLabel={cms[264787]}
                            onChange={NoOp}
                            onClick={onInputClickHandler}
                            onKeyDown={onInputClickHandler}
                            inputText={query}
                            variant="input"
                            ref={inputRef}
                            aria-label={cms[238402]}
                        />
                    ) : (
                        <>
                            {props.screenSize.tabletOrDesktop ? (
                                <SearchBoxPopover
                                    query={query}
                                    setSearchText={setSearchText}
                                    setSelectedResult={onSearchResultSelect}
                                    onClearSearch={onClearTextInputClick}
                                    dataElementName="activities-landing-text-input"
                                    isHomeSearchBox={true}
                                    placeholderText={props.placeholderText}
                                    onGlobalKeywordResultSelect={onGlobalKeywordResultSelect}
                                />
                            ) : (
                                <SearchField
                                    data-testid="activities-landing-text-input"
                                    data-element-name="activities-landing-text-input"
                                    variant="input"
                                    placeholderText={props.placeholderText}
                                    ref={inputRef}
                                    inputText={query}
                                    onChange={NoOp}
                                    leadingAsset={{
                                        id: 'fill.symbol.search',
                                    }}
                                    label={cms[238402]}
                                    clearLabel={cms[264787]}
                                    onClick={onPopoverInputClickHandler}
                                    onKeyDown={onPopoverInputClickHandler}
                                    role="textbox"
                                />
                            )}
                        </>
                    )}
                    {/* Hide button when experiment ACT-4519 is B and Desktop */}
                    {props.screenSize.desktop && shouldHideSearchBoxExperiment ? null : (
                        <Button
                            mt="12"
                            data-testid="activities-landing-search-button"
                            data-element-name="activities-landing-search-button"
                            onClick={onSearchButtonClick}
                            label={cms[126799]}
                            variant="label"
                        />
                    )}
                </Box>
            </Feature.Off>
            <Feature.On name={FeatureName.HomePageV2Layout}>
                <Box data-testid="search-box-wrapper" display="flex" flexDirection="row" alignItems="center" width="100%">
                    {!props.isSearchBoxDropdown ? (
                        <SearchField
                            data-testid="activities-landing-text-input"
                            data-element-name="activities-landing-text-input"
                            placeholderText={props.placeholderText}
                            leadingAsset={{ id: 'line.symbol.search' }}
                            clearLabel={cms[264787]}
                            label={props.placeholderText}
                            onChange={NoOp}
                            onClick={onInputClickHandler}
                            onKeyDown={onInputClickHandler}
                            inputText={query}
                            variant="input"
                            ref={inputRef}
                            aria-label={cms[238402]}
                        />
                    ) : (
                        <>
                            {props.screenSize.tabletOrDesktop ? (
                                <SearchBoxPopover
                                    query={query}
                                    setSearchText={setSearchText}
                                    setSelectedResult={onSearchResultSelect}
                                    onClearSearch={onClearTextInputClick}
                                    dataElementName="activities-landing-text-input"
                                    isHomeSearchBox={true}
                                    isNewHomePageEnabled={true}
                                    placeholderText={props.placeholderText}
                                    onGlobalKeywordResultSelect={onGlobalKeywordResultSelect}
                                />
                            ) : (
                                <SearchField
                                    data-testid="activities-landing-text-input"
                                    data-element-name="activities-landing-text-input"
                                    variant="picker"
                                    placeholderText={props.placeholderText}
                                    ref={inputRef}
                                    inputText={query}
                                    onChange={NoOp}
                                    leadingAsset={{
                                        id: 'fill.symbol.search',
                                    }}
                                    label={cms[238402]}
                                    onClick={onPopoverInputClickHandler}
                                    onKeyDown={onPopoverInputClickHandler}
                                    role="textbox"
                                />
                            )}
                        </>
                    )}
                    {/* Hide button when experiment ACT-4519 is B and Desktop */}
                    {props.screenSize.desktop && shouldHideSearchBoxExperiment ? null : (
                        <Button
                            ms="8"
                            data-testid="activities-landing-search-button"
                            data-element-name="activities-landing-search-button"
                            onClick={onSearchButtonClick}
                            label={cms[126799]}
                            variant={searchButtonVariant}
                            leadingIcon={searchButtonLeadingIcon}
                        />
                    )}
                </Box>
            </Feature.On>
        </>
    );
};

SearchBox.displayName = 'SearchBox';

export default connect(mapStateToProps, mapDispatchToProps)(SearchBox);
